'use client';
import React from 'react';
import Link from 'next/link';
import Button from '../ui/Button';
import { motion } from 'framer-motion';
import Image from 'next/image';

const HeroSection = () => {
  return (
    <section className="rpl-hero-section bg-[#2d1a0a]">

      {/* Background base */}
      <div className="absolute inset-0 z-0 bg-[#2d1a0a]" />

      {/* Full-width background image */}
      <div className="absolute inset-0 w-full h-full z-[1] pointer-events-none pt-[var(--navbar-h)]">
        <div className="relative w-full h-full">
          <img
            src="/images/hero-new-luxury.webp"
            alt="Royal Prime Labels Premium Gold Foiled Bottles"
            className="img-fill object-right-top brightness-[0.95] contrast-[1.02] saturate-[1.04]"
            loading="eager"
            fetchPriority="high"
          />
        </div>
      </div>

      {/* Left-side dark overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent z-[2] pointer-events-none" />

      {/* Content Frame — uses CSS token classes */}
      <div className="rpl-hero-frame">
        <div className="flex justify-start items-center w-full">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="rpl-hero-card"
          >
            {/* Premium Glass Card */}
            <div className="rpl-hero-card-inner">
              {/* Animated top highlight */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

              <div className="flex flex-col items-start w-full relative z-10 gap-fluid-lg">

                {/* Text Group */}
                <div className="flex flex-col items-start w-full font-sans gap-fluid-md">

                  {/* Eyebrow */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                  >
                    <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/[0.05] border border-white/10 text-white/90 type-meta">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#3672ae] mr-3 animate-pulse" />
                      Established 2014
                    </span>
                  </motion.div>

                  {/* Headline — uses .rpl-hero-headline CSS class for 4-tier system */}
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.8 }}
                    className="rpl-hero-headline text-left w-full"
                  >
                    <span className="block font-serif italic text-white mb-2">Custom Label Manufacturer</span>
                    <span className="block mb-1">in India</span>
                    <span className="block text-[#C9A84C] font-semibold tracking-wider uppercase mt-2 sm:mt-4" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.5rem)' }}>
                      Roll Form, Pharma &amp; FMCG Labels
                    </span>
                  </motion.h1>

                  {/* Description — uses .rpl-hero-body CSS class */}
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                    className="rpl-hero-body text-left"
                  >
                    Building <span className="text-white font-medium">premium brand identities</span> to resource global industries. We focus on fast delivery and high quality for every customised order.
                  </motion.p>

                  {/* Trust Stats */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 0.8 }}
                    className="rpl-hero-stats"
                  >
                    {[
                      { label: "Delivery", value: "5-7 Days", fullLabel: "Delivery Cycle" },
                      { label: "Export Ready", value: "Global", fullLabel: "Export Ready" },
                      { label: "Design & Dev", value: "In House", fullLabel: "Design & Development" }
                    ].map((badge, idx) => (
                      <div key={idx} className="flex flex-col gap-0.5 border-l border-white/20 pl-4 first:border-0 first:pl-0">
                        <span className="text-white font-bold leading-none" style={{ fontSize: 'clamp(0.875rem, 1.1vw, 1.125rem)' }}>{badge.value}</span>
                        <span className="text-white/50 font-bold tracking-wider sm:tracking-widest uppercase" style={{ fontSize: 'clamp(8px, 0.6vw, 10px)' }}>
                          <span className="hidden sm:inline">{badge.fullLabel}</span>
                          <span className="inline sm:hidden">{badge.label}</span>
                        </span>
                      </div>
                    ))}
                  </motion.div>
                </div>

                {/* CTA Buttons — .rpl-hero-buttons handles flex-col→row via CSS */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1, duration: 0.8 }}
                  className="rpl-hero-buttons"
                >
                  <Link href="/quote" className="no-tap-enforce w-full sm:w-auto">
                    <Button
                      variant="primary"
                      className="w-full sm:w-auto bg-white hover:bg-white/95 text-slate-950 rounded-full px-6 py-3 sm:px-8 sm:py-4 font-bold tracking-wider uppercase transition-all duration-300 shadow-[0_20px_40px_rgba(255,255,255,0.1)] hover:shadow-[0_25px_50px_rgba(255,255,255,0.2)] hover:-translate-y-1"
                      style={{ fontSize: 'clamp(0.75rem, 0.9vw, 0.9375rem)' }}
                    >
                      Get a Quote
                    </Button>
                  </Link>
                  <Link href="/portfolio" className="no-tap-enforce w-full sm:w-auto">
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto bg-white/5 border border-white/20 text-white hover:bg-white/10 rounded-full px-6 py-3 sm:px-8 sm:py-4 font-bold tracking-wider uppercase transition-all duration-300 backdrop-blur-sm hover:border-white/40"
                      style={{ fontSize: 'clamp(0.75rem, 0.9vw, 0.9375rem)' }}
                    >
                      Explore Gallery
                    </Button>
                  </Link>
                </motion.div>

              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 12, 0], opacity: [0.2, 0.5, 0.2] }}
        transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-4"
      >
        <div className="w-px h-24 bg-gradient-to-b from-white/40 via-white/20 to-transparent" />
        <span className="text-white/30 uppercase font-medium" style={{ fontSize: '9px', letterSpacing: '0.4em' }}>Scroll to explore</span>
      </motion.div>

    </section>
  );
};

export default HeroSection;